import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, FileJson, FileText, Package, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

async function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
}

function toCSV(rows) {
    if (!rows.length) return "";
    const keys = Array.from(rows.reduce((s, r) => { Object.keys(r || {}).forEach(k => s.add(k)); return s; }, new Set()));
    const esc = (v) => {
        if (v == null) return "";
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [keys.join(","), ...rows.map(r => keys.map(k => esc(r[k])).join(","))].join("\n");
}

export default function DataExportTab() {
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/backup/summary").then(r => setSummary(r.data)); }, []);

    const downloadFullBackup = async (format = "json") => {
        setBusy(true);
        try {
            const { data } = await api.get("/backup/full");
            const stamp = farmDateIso();
            if (format === "json") {
                await downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), `ajfarm-backup-${stamp}.json`);
            } else if (format === "excel") {
                const XLSX = await import("xlsx");
                const wb = XLSX.utils.book_new();
                Object.entries(data.collections).forEach(([name, rows]) => {
                    if (!rows.length) return;
                    const flat = rows.map(r => {
                        const out = {};
                        Object.entries(r).forEach(([k, v]) => { out[k] = typeof v === "object" && v !== null ? JSON.stringify(v) : v; });
                        return out;
                    });
                    const ws = XLSX.utils.json_to_sheet(flat);
                    XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
                });
                const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
                await downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `ajfarm-backup-${stamp}.xlsx`);
            }
            toast.success(`Backup downloaded (${format.toUpperCase()})`);
        } catch (e) { toast.error("Backup failed"); }
        setBusy(false);
    };

    const exportCollection = async (coll, format) => {
        try {
            const path = coll === "farm" ? "/farm" : `/${coll.replace(/_/g, "-")}`;
            const { data } = await api.get(path);
            const rows = Array.isArray(data) ? data : [data];
            const stamp = farmDateIso();
            if (format === "csv") {
                await downloadBlob(new Blob([toCSV(rows)], { type: "text/csv" }), `${coll}-${stamp}.csv`);
            } else if (format === "json") {
                await downloadBlob(new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" }), `${coll}-${stamp}.json`);
            } else if (format === "excel") {
                const XLSX = await import("xlsx");
                const wb = XLSX.utils.book_new();
                const flat = rows.map(r => {
                    const out = {};
                    Object.entries(r || {}).forEach(([k, v]) => { out[k] = typeof v === "object" && v !== null ? JSON.stringify(v) : v; });
                    return out;
                });
                const ws = XLSX.utils.json_to_sheet(flat);
                XLSX.utils.book_append_sheet(wb, ws, coll.slice(0, 31));
                const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
                await downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${coll}-${stamp}.xlsx`);
            }
            toast.success(`${coll} exported as ${format.toUpperCase()}`);
        } catch (e) { toast.error(`Export failed: ${e.response?.data?.detail || e.message}`); }
    };

    const COLLECTIONS = [
        { key: "species", label: "Species" },
        { key: "animals", label: "Animals" },
        { key: "employees", label: "Employees" },
        { key: "tasks", label: "Tasks" },
        { key: "transactions", label: "Transactions" },
        { key: "sales", label: "Sales" },
        { key: "inventory_items", label: "Inventory Items" },
        { key: "health_records", label: "Health Records" },
        { key: "breeding_records", label: "Breeding Records" },
        { key: "attendance", label: "Attendance" },
    ];

    return (
        <div className="space-y-6" data-testid="data-export-tab">
            <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <Package className="w-6 h-6 text-primary" />
                    <div>
                        <h3 className="text-xl font-semibold">{i18n.t("batch4.e1301ae09132")}</h3>
                        <p className="text-sm text-muted-foreground">{i18n.t("batch4.62b199007f1d")}</p>
                    </div>
                </div>
                {summary && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-5 text-sm">
                        {Object.entries(summary.counts).map(([k, v]) => (
                            <div key={k} className="p-3 rounded-xl bg-secondary">
                                <div className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, " ")}</div>
                                <div className="font-mono font-bold text-lg">{v}</div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex gap-3 flex-wrap">
                    <Button className="h-12" disabled={busy} onClick={() => downloadFullBackup("json")} data-testid="backup-json-btn">
                        <FileJson className="w-4 h-4 mr-2" /> {i18n.t("batch4.d296a30a06e6")}
                    </Button>
                    <Button className="h-12" variant="outline" disabled={busy} onClick={() => downloadFullBackup("excel")} data-testid="backup-excel-btn">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> {i18n.t("batch4.e184a42b6a65")}
                    </Button>
                </div>
            </Card>

            <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <Download className="w-6 h-6 text-primary" />
                    <div>
                        <h3 className="text-xl font-semibold">{i18n.t("batch4.6d9f4c28dd8a")}</h3>
                        <p className="text-sm text-muted-foreground">{i18n.t("batch4.aeda8054074a")}</p>
                    </div>
                </div>
                <div className="space-y-2">
                    {COLLECTIONS.map(c => (
                        <div key={c.key} className="flex items-center justify-between p-3 rounded-xl border border-border hover:bg-secondary transition-colors">
                            <div className="font-semibold">{c.label} <span className="text-xs text-muted-foreground font-mono">({summary?.counts?.[c.key] ?? 0})</span></div>
                            <div className="flex gap-1.5">
                                <Button size="sm" variant="outline" onClick={() => exportCollection(c.key, "csv")} data-testid={`export-${c.key}-csv`}><FileText className="w-3.5 h-3.5 mr-1" /> {i18n.t("batch4.3281188351b8")}</Button>
                                <Button size="sm" variant="outline" onClick={() => exportCollection(c.key, "excel")} data-testid={`export-${c.key}-excel`}><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {i18n.t("batch4.a08e11119aef")}</Button>
                                <Button size="sm" variant="outline" onClick={() => exportCollection(c.key, "json")} data-testid={`export-${c.key}-json`}><FileJson className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="p-6 bg-primary/5 border-primary/20">
                <div className="flex items-center gap-3">
                    <CloudUpload className="w-6 h-6 text-primary" />
                    <div className="flex-1">
                        <h3 className="text-xl font-semibold">{i18n.t("batch4.46e72c33d770")}</h3>
                        <p className="text-sm text-muted-foreground">{i18n.t("batch4.f8ed4fb0315f")} <strong>{i18n.t("batch4.07c2964c41f4")}</strong> {i18n.t("batch4.f3ddba3372dd")}</p>
                    </div>
                </div>
            </Card>
        </div>
    );
}
